/**
 * City data loader — tries the R2 precomputed bucket first, falls back
 * to the live Overpass extraction pipeline on miss.
 *
 * Why this exists: fetching a precomputed CityData JSON from R2 is
 * ~100× faster than round-tripping Overpass (single gzipped download
 * from a CDN vs multiple rate-limited queries against a public API).
 * For the 37 curated cities the precomputed file always exists; for
 * practice-mode user-typed cities the fallback Overpass path still
 * runs.
 *
 * The shape of CityData returned is identical regardless of source —
 * the upload script uses the same processCityData() function, so
 * callers don't have to branch.
 */

import type { CityBounds, CityData, LatLng, NavEdge } from '../types/map';
import { extractCityData, computeBounds } from './overpass';
import { processCityData } from './processor';
import { haversineDistance } from './graphBuilder';

/** Must match the slug scheme used by scripts/build-and-upload-cities.ts. */
export function citySlug(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '') // strip diacritics
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export type LoadProgress = (stage: string, progress: number) => void;

/**
 * Load processed city data by name + center. Returns the CityData object
 * ready to hand to MapRenderer. Caller gets progress updates via the
 * optional callback — stages use human-readable strings (e.g. "Loading
 * from CDN…", "Querying Overpass…", "Processing map data…").
 *
 * Resolution order:
 *   1. R2: `${VITE_R2_URL}/<slug>.json`  — ~1s, always used when available
 *   2. Overpass → processor                 — slow fallback, 10-100s
 */
export async function loadCityData(
  cityName: string,
  center: LatLng,
  bounds: CityBounds | undefined,
  onProgress?: LoadProgress,
): Promise<CityData> {
  const computedBounds = computeBounds(center, bounds);
  const r2Base = import.meta.env.VITE_R2_URL as string | undefined;

  // Try R2 first if configured. Swallow any error (404, network, CORS) and
  // fall through to the Overpass path so the game stays functional in
  // every case.
  if (r2Base) {
    const slug = citySlug(cityName);
    const url = `${r2Base}/${slug}.json`;
    onProgress?.('Loading map data from CDN…', 0.1);
    try {
      const res = await fetch(url);
      if (res.ok) {
        onProgress?.('Loading map data from CDN…', 0.6);
        const data = (await res.json()) as CityData;
        // Sanity check: the precomputed JSON should contain everything we
        // need. If the shape is off (e.g. bucket holds a stale/broken
        // file), fall through to the live pipeline rather than crash.
        if (data && data.layers && data.navigation) {
          onProgress?.('Map ready.', 0.99);
          // Run city-specific post-load patches (e.g. Venice bridge stitching)
          // before handing the data to the game. Cheap enough to do on every
          // load — fast-exit for cities with no patches defined.
          return applyCityPatches(data);
        }
        console.warn(`[cityLoader] R2 returned malformed data for ${cityName}, falling back to Overpass.`);
      } else if (res.status !== 404) {
        console.warn(`[cityLoader] R2 returned ${res.status} for ${cityName}, falling back to Overpass.`);
      }
      // 404: city not on R2 (e.g. practice-mode free-typed city). Silent fallthrough.
    } catch (err) {
      console.warn(`[cityLoader] R2 fetch failed for ${cityName}, falling back to Overpass:`, err);
    }
  }

  // Fallback: live Overpass extraction + client-side processing.
  const rawData = await extractCityData(computedBounds, (stage, progress) => {
    onProgress?.(stage, progress);
  });
  onProgress?.('Processing map data…', 0.98);
  await new Promise((r) => setTimeout(r, 50));
  const processed = processCityData(rawData, cityName, center, computedBounds);
  return applyCityPatches(processed);
}

// ─────────────────────────────────────────────────────────────────────────
// City-specific runtime patches
// ─────────────────────────────────────────────────────────────────────────

/**
 * Apply any post-load fixups that are specific to individual cities and
 * can't (or shouldn't) be baked into the precomputed R2 JSON yet.
 *
 * This is the entry point every caller should go through. Currently only
 * Venice needs a patch; additional cities can be added here without
 * touching the R2 upload pipeline or invalidating the cached JSONs.
 */
function applyCityPatches(data: CityData): CityData {
  const slug = citySlug(data.name);
  if (slug === 'venice') return patchVeniceBridges(data);
  return data;
}

/**
 * Venice-specific: stitch footbridge endpoints to the nearby land paths
 * so the player can actually cross canals.
 *
 * Why: Venice's OSM data marks canal footbridges as isolated footpath
 * geometry — the bridge ends don't share coordinates with the walkway
 * edges on either shore, so when the nav-graph builder snaps nodes by
 * lat/lng key the bridge becomes an island. Pathfinding can't cross,
 * and every canal is effectively a game-wall.
 *
 * The fix walks every edge tagged `isBridge` and, for each endpoint
 * node, searches for the nearest other node within a small radius
 * (STITCH_RADIUS_M) that isn't already connected to it. If found, a
 * synthetic footpath edge is inserted to bridge the gap. Both endpoint
 * nodes' `.edges` arrays are updated so pathfinding sees the new link
 * from either side.
 *
 * Kept in cityLoader (not the processor/build step) deliberately so we
 * don't have to re-extract + re-upload all 37 precomputed city JSONs
 * when only Venice is broken. Runs fast (O(B×N) where B = bridge count
 * and N = nodes, both small per city).
 */
function patchVeniceBridges(data: CityData): CityData {
  // Radius around each bridge endpoint to look for a land node to stitch
  // to. Venice canals + narrow calli can leave fairly big geometric gaps
  // between the bridge endpoint and the nearest pedestrian node — 30m was
  // not enough in practice. 80m is still small enough that we won't
  // accidentally short-circuit across a whole canal mouth or sestiere.
  const STITCH_RADIUS_M = 80;
  const { nodes, edges } = data.navigation;

  // Build an adjacency lookup: nodeId → set of nodeIds it's already
  // directly connected to. Used to skip pairs that already have an edge,
  // and to dedupe stitches we add within this pass.
  const connected = new Map<string, Set<string>>();
  for (const n of nodes) connected.set(n.id, new Set());
  for (const e of edges) {
    connected.get(e.from)?.add(e.to);
    connected.get(e.to)?.add(e.from);
  }

  // Index nodes by id for constant-time lookup during the stitching scan.
  const nodeById = new Map<string, typeof nodes[number]>();
  for (const n of nodes) nodeById.set(n.id, n);

  const bridgeEdges = edges.filter(e => e.isBridge);
  const newEdges: NavEdge[] = [];
  let stitchCount = 0;

  for (const bridge of bridgeEdges) {
    for (const endpointId of [bridge.from, bridge.to]) {
      const endpoint = nodeById.get(endpointId);
      if (!endpoint) continue;

      // Find the nearest node that isn't this endpoint AND isn't already
      // connected to it. We also skip the bridge's OTHER endpoint so we
      // don't just re-link the bridge to itself.
      const otherEndId = endpointId === bridge.from ? bridge.to : bridge.from;
      let nearest: typeof nodes[number] | null = null;
      let nearestDist = STITCH_RADIUS_M + 1;
      for (const n of nodes) {
        if (n.id === endpointId || n.id === otherEndId) continue;
        if (connected.get(endpointId)?.has(n.id)) continue;
        const d = haversineDistance(endpoint.lat, endpoint.lng, n.lat, n.lng);
        if (d < nearestDist) {
          nearest = n;
          nearestDist = d;
        }
      }

      if (!nearest) continue;

      const newEdge: NavEdge = {
        id: `venice-stitch-${endpointId}-${nearest.id}`,
        from: endpointId,
        to: nearest.id,
        length: nearestDist,
        classification: 'footpath',
        signalWeight: 0.1, // matches footpath's canonical weight
        zone: 'mixed-use',
        isStraight: true,
        isOneWay: false,
        isBridge: false,
        isTunnel: false,
        geometry: [
          [endpoint.lng, endpoint.lat],
          [nearest.lng, nearest.lat],
        ],
      };
      newEdges.push(newEdge);
      // Mutate the endpoint nodes' edge lists so existing lookups see
      // the new link immediately. Both sides get the id — the graph is
      // undirected for pathfinding purposes.
      endpoint.edges.push(newEdge.id);
      nearest.edges.push(newEdge.id);
      connected.get(endpointId)?.add(nearest.id);
      connected.get(nearest.id)?.add(endpointId);
      stitchCount++;
    }
  }

  if (stitchCount > 0) {
    // eslint-disable-next-line no-console
    console.log(`[cityLoader] Venice bridge patch: stitched ${stitchCount} synthetic edges across ${bridgeEdges.length} bridges.`);
  }

  return {
    ...data,
    navigation: {
      ...data.navigation,
      edges: [...edges, ...newEdges],
    },
  };
}
